'use client'

import { ReactNode, useEffect, useState } from 'react'
import { ThemeProvider } from 'next-themes'
import { usePathname } from 'next/navigation'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { I18nProvider } from '@/components/I18nProvider'
import { OfflineBanner } from '@/components/OfflineBanner'
import { useAuthStore } from '@/context/auth.store'
import { operationalTelemetry } from '@/lib/operational-telemetry'

interface ProvidersProps {
  children: ReactNode
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error: unknown) => {
        const status = (error as AxiosError | undefined)?.response?.status
        if (status === 404 || status === 503 || status === 401 || status === 403) {
          return false
        }
        return count < 1
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

export function Providers({ children }: ProvidersProps) {
  const pathname = usePathname()
  const bootstrap = useAuthStore((state) => state.bootstrap)
  const logout = useAuthStore((state) => state.logout)
  const authUser = useAuthStore((state) => state.user)
  const authStatus = useAuthStore((state) => state.status)
  const persist = (useAuthStore as unknown as {
    persist?: {
      hasHydrated?: () => boolean
      onFinishHydration?: (listener: () => void) => () => void
    }
  }).persist
  const [authHydrated, setAuthHydrated] = useState(persist?.hasHydrated?.() ?? true)
  const currentPath = pathname || '/'
  const isPublicBootstrapRoute =
    currentPath === '/' ||
    currentPath.startsWith('/a/') ||
    currentPath === '/auth' ||
    currentPath === '/login' ||
    currentPath.startsWith('/login/') ||
    currentPath === '/register' ||
    currentPath.startsWith('/register/') ||
    currentPath === '/forgot-password' ||
    currentPath.startsWith('/forgot-password/') ||
    currentPath === '/reset-password' ||
    currentPath.startsWith('/reset-password/') ||
    currentPath === '/privacy' ||
    currentPath.startsWith('/legal/') ||
    currentPath === '/terms' ||
    currentPath.startsWith('/public/') ||
    currentPath === '/waitlist' ||
    currentPath.startsWith('/waitlist/') ||
    currentPath === '/verify-email'
  const forceLightTheme =
    isPublicBootstrapRoute

  useEffect(() => {
    if (!forceLightTheme) return
    const root = document.documentElement
    root.classList.remove('modern')
  }, [forceLightTheme, pathname])

  useEffect(() => {
    if (!persist?.onFinishHydration) {
      setAuthHydrated(true)
      return
    }
    const unsub = persist.onFinishHydration(() => setAuthHydrated(true))
    return unsub
  }, [persist])

  /**
   * CRITICAL EFFECT: Bootstrap auth and connect to SessionAuthority
   *
   * This effect handles the complete bootstrap lifecycle:
   * 1. Mark session as VALIDATING
   * 2. Call auth.store.bootstrap()
   * 3. If user exists → validateSession in SessionAuthority
   * 4. If user null → invalidateSession in SessionAuthority
   *
   * This connection is ESSENTIAL to prevent deadlock in SessionAuthorityBoundary.
   */
  useEffect(() => {
    if (!authHydrated || isPublicBootstrapRoute) return
    void (async () => {
      try {
        // Import SessionAuthority (deferred to avoid circular deps at module load)
        const { useSessionAuthority } = await import('@/context/session.authority')

        // Mark that bootstrap is starting
        useSessionAuthority.getState().setValidating()
        console.log('[Providers] Bootstrap starting, SessionAuthority set to VALIDATING')

        // Run auth bootstrap
        await bootstrap()
        console.log('[Providers] Bootstrap completed, checking auth state')

        // Get current auth state and SessionAuthority
        const authState = useAuthStore.getState()
        const sessionAuthority = useSessionAuthority.getState()

        // PHASE 1: Check if logout happened during bootstrap
        // If so, don't try to validate session
        if (sessionAuthority.status === 'LOGGING_OUT' || sessionAuthority.status === 'LOGGED_OUT') {
          console.log('[Providers] Logout happened during bootstrap, skipping session resolution')
          return
        }

        if (authState.user) {
          // User exists - validate session
          console.log('[Providers] Auth user exists, validating session:', authState.user.id)
          useSessionAuthority.getState().validateSession({
            id: authState.user.id,
            sessionId: authState.user.id, // Use userId as sessionId for now
          })
        } else {
          // PHASE 1 FIX: "No user" is NOT an invalid session — it's simply not authenticated
          // setLoggedOut() is non-destructive: no teardown, no cache clear, no spam
          // Was: invalidateSession('bootstrap-no-user')  ← WRONG: triggered RuntimeTeardown
          console.log('[Providers] No auth user found after bootstrap, setting LOGGED_OUT (non-destructive)')
          useSessionAuthority.getState().setLoggedOut()
        }
      } catch (error) {
        console.error('[Providers] Bootstrap error:', error)
        // Invalidate on error, but only if not already in logout lifecycle
        const { useSessionAuthority } = await import('@/context/session.authority')
        const sessionStatus = useSessionAuthority.getState().status
        if (sessionStatus !== 'LOGGING_OUT' && sessionStatus !== 'LOGGED_OUT' && sessionStatus !== 'INVALID') {
          useSessionAuthority.getState().invalidateSession('bootstrap-error')
        } else {
          console.log('[Providers] Bootstrap error occurred but already in logout lifecycle, skipping invalidation')
        }
      }
    })()
  }, [authHydrated, bootstrap, isPublicBootstrapRoute])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const channelName = 'bpal-auth'
    const handleMessage = async (event: MessageEvent) => {
      const type = String((event.data as { event?: string } | null)?.event || '')
      
      if (type === 'logout') {
        const authState = useAuthStore.getState()
        const { useSessionAuthority } = await import('@/context/session.authority')
        const sessionStatus = useSessionAuthority.getState().status

        // PHASE 1: Guard 1 — Already in logout lifecycle
        if (sessionStatus === 'LOGGING_OUT' || sessionStatus === 'LOGGED_OUT') {
          console.log('[Providers] BroadcastChannel logout received — already in logout lifecycle, skipping')
          return
        }

        // PHASE 1: Guard 2 — User already not authenticated
        if (!authState.user && !authState.isAuthenticated) {
          console.log('[Providers] BroadcastChannel logout received — user already null/not authenticated, skipping')
          return
        }

        console.log('[Providers] BroadcastChannel logout received, user authenticated, calling logout()')
        await logout()
      }
      
      if (type === 'login') {
        if (isPublicBootstrapRoute) return
        void bootstrap().catch(() => undefined)
      }
    }

    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel(channelName)
      channel.onmessage = handleMessage
      return () => channel.close()
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== channelName || !event.newValue) return
      try {
        const payload = JSON.parse(event.newValue) as { event?: string }
        void handleMessage({ data: payload } as MessageEvent)
      } catch {
        // noop
      }
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [bootstrap, logout, isPublicBootstrapRoute])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const onError = (event: ErrorEvent) => {
      const message = String(event.message || '')
      const source = String(event.filename || '')
      const isDomInsertError =
        message.includes('Node.insertBefore') ||
        message.includes('NotFoundError: Node.insertBefore')
      const isAutofillOverlay = source.includes('bootstrap-autofill-overlay')

      if (!isDomInsertError && !isAutofillOverlay) return

      operationalTelemetry.recordClientDomAnomaly('dom-insertbefore-failure', {
        message,
        source,
        pathname: window.location.pathname,
      })
    }

    window.addEventListener('error', onError)
    return () => window.removeEventListener('error', onError)
  }, [])

  /**
   * Keep SessionAuthority in sync after in-app login transitions.
   *
   * Without this, the app can stay stuck in LOGGED_OUT until full refresh,
   * because Providers bootstrap effect runs only on initial mount.
   *
   * GUARD: Do not re-validate if SessionAuthority is already LOGGED_OUT.
   * This prevents a race where refreshAuthority() resurrects authenticated state
   * after explicit logout.
   */
  useEffect(() => {
    if (authStatus !== 'AUTH_AUTHENTICATED' || !authUser?.id) return

    void (async () => {
      const { useSessionAuthority } = await import('@/context/session.authority')
      const sessionState = useSessionAuthority.getState()

      // GUARD: Do not re-validate after logout or session termination.
      // LOGGED_OUT, RUNTIME_TERMINATED, and INVALID are all terminal/post-logout states
      // that must not be overwritten by async auth refreshes (auth resurrection bug).
      const terminalStates = ['LOGGED_OUT', 'RUNTIME_TERMINATED', 'INVALID'] as const
      if (terminalStates.includes(sessionState.status as any)) {
        console.log(`[Providers] Auth is authenticated, but SessionAuthority is ${sessionState.status} (terminal) — blocking re-validation`)
        return
      }

      if (sessionState.status === 'VALID' || sessionState.status === 'LOGGING_OUT') return

      console.log('[Providers] Auth is authenticated, re-validating SessionAuthority')
      useSessionAuthority.getState().validateSession({
        id: authUser.id,
        sessionId: authUser.id,
      })
    })()
  }, [authStatus, authUser?.id])

  return (
    <I18nProvider>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        forcedTheme={forceLightTheme ? 'light' : undefined}
      >
        <OfflineBanner />
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </ThemeProvider>
    </I18nProvider>
  )
}
